// i18n
import { translateInComponent } from 'infras/i18n';

import { useState, useEffect, SyntheticEvent, memo } from 'react';
import BusinessProfileForm from 'presentations/views/components/organisms/BusinessProfileForm';
import translations from 'presentations/views/components/organisms/BusinessProfileForm/i18n.json';
import { ErrorList } from 'domains/models';
import Snackbar, {
  SeverityType,
} from 'presentations/views/components/atoms/Snackbar';
import { post, get, patch } from 'infras/rest_client';
import { apiPath, helpPath, webPath } from 'presentations/routes/path';
import { ViewerItem } from 'presentations/api/schema/user';
import BusinessProfile from 'domains/models/business_profile';
import {
  BusinessProfileCreateBody,
  BusinessProfileItem,
  BusinessProfileUpdateBody,
} from 'presentations/api/schema/business_profile';
import { IntegrationMergedTeamShopAccountItem } from 'presentations/api/schema/team_shop_account';
import { setEvent } from 'presentations/views/helpers/analytics';

interface Props {
  id?: string;
  isOpen: boolean;
  viewer: ViewerItem;
  businessProfile: BusinessProfileItem | null;
  shopAccount: IntegrationMergedTeamShopAccountItem | null;
  onSave: any;
  onCancel: any;
  onEdit: any;
}

const Component = memo((props: Props) => {
  const businessProfile = props.shopAccount?.stripeBusinessProfile;
  const t = translateInComponent(translations);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [severity, setSeverity] = useState<SeverityType>(SeverityType.Success);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorList>({});

  // ServicePrice
  const [name, setName] = useState<string | null>(
    businessProfile?.name || props.viewer?.team?.name || null,
  );
  const [owner, setOwner] = useState<string | null>(props.viewer.name || null);
  const [address, setAddress] = useState<string | null>(
    businessProfile?.address || null,
  );
  const [tel, setTel] = useState<string | null>(businessProfile?.tel || null);
  const [priceDescription, setPriceDescription] = useState<string | null>(
    t('priceDescriptionDefault'),
  );
  const [paymentMethodDescription, setPaymentMethodDescription] = useState<
    string | null
  >(t('paymentMethodDescriptionDefault'));
  const [paymentScheduleDescription, setPaymentScheduleDescription] = useState<
    string | null
  >(t('paymentScheduleDescriptionDefault'));
  const [feeDescription, setFeeDescription] = useState<string | null>(
    t('feeDescriptionDefault'),
  );
  const [serviceDescription, setServiceDescription] = useState<string | null>(
    t('serviceDescriptionDefault'),
  );
  const [refundDescription, setRefundDescription] = useState<string | null>(
    t('refundDescriptionDefault'),
  );
  const [environmentDescription, setEnvironmentDescription] = useState<
    string | null
  >(`${t('environmentDescriptionDefault')}\n${webPath(helpPath('7155166'))}`);

  useEffect(() => {
    if (!props.businessProfile) return;
    setName(props.businessProfile.name);
    setOwner(props.businessProfile.owner);
    setAddress(props.businessProfile.address);
    setTel(props.businessProfile.tel);

    setPriceDescription(
      props.businessProfile.priceDescription || t('priceDescriptionDefault'),
    );
    setPaymentMethodDescription(
      props.businessProfile.paymentMethodDescription ||
        t('paymentMethodDescriptionDefault'),
    );
    setPaymentScheduleDescription(
      props.businessProfile.paymentScheduleDescription ||
        t('paymentScheduleDescriptionDefault'),
    );
    setFeeDescription(
      props.businessProfile.feeDescription || t('feeDescriptionDefault'),
    );
    setServiceDescription(
      props.businessProfile.serviceDescription ||
        t('serviceDescriptionDefault'),
    );
    setRefundDescription(
      props.businessProfile.refundDescription || t('refundDescriptionDefault'),
    );
    setEnvironmentDescription(
      props.businessProfile.environmentDescription ||
        `${t('environmentDescriptionDefault')}\n${webPath(
          helpPath('7155166'),
        )}`,
    );
  }, [props.businessProfile]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);

    const businessProfile = {
      name: name!,
      owner: owner!,
      address: address!,
      tel: tel!,
      priceDescription: priceDescription!,
      paymentMethodDescription: paymentMethodDescription!,
      paymentScheduleDescription: paymentScheduleDescription!,
      feeDescription: feeDescription!,
      serviceDescription: serviceDescription!,
      refundDescription: refundDescription!,
      environmentDescription: environmentDescription!,
    };

    if (props.businessProfile) {
      const newBusinessProfile = new BusinessProfile(businessProfile);
      if (!newBusinessProfile.isValid(Object.keys(businessProfile))) {
        setErrors(newBusinessProfile.errors);
        setIsLoading(false);
        return;
      }

      const data: BusinessProfileUpdateBody = businessProfile;
      const result = await patch(
        apiPath(`/v1/teams/${props.viewer.team!.uid}/business_profile/update`),
        { data },
      );
      if (!result.isSuccess) {
        setErrors(result.errors);
        setMessage(t('Error'));
        setSeverity(SeverityType.Error);
        setIsLoading(false);
        return;
      }

      setSeverity(SeverityType.Success);
      setMessage(t('Updated'));
      setIsLoading(false);
    } else {
      const newBusinessProfile = new BusinessProfile(businessProfile);
      if (!newBusinessProfile.isValid(Object.keys(businessProfile))) {
        setErrors(newBusinessProfile.errors);
        setIsLoading(false);
        return;
      }

      const data: BusinessProfileCreateBody = businessProfile;
      const result = await post(
        apiPath(`/v1/teams/${props.viewer.team!.uid}/business_profile/create`),
        { data },
      );
      if (!result.isSuccess) {
        setErrors(result.errors);
        setMessage(t('Error'));
        setSeverity(SeverityType.Error);
        setIsLoading(false);
        return;
      }

      setSeverity(SeverityType.Success);
      setMessage(t('Created'));
      setIsLoading(false);
      setEvent('payment host create');
    }

    props.onSave();
  };

  const onCancel = async (e: SyntheticEvent) => {
    props.onCancel();
  };

  const onEdit = async (e: SyntheticEvent) => {
    props.onEdit();
  };

  const onChange = (e: SyntheticEvent<any>, option: any) => {
    const prop = String(e.currentTarget.name);
    const value = e.currentTarget.value;
    const businessProfile = {
      name: name!,
      owner: owner!,
      address: address!,
      tel: tel!,
      priceDescription,
      paymentMethodDescription,
      paymentScheduleDescription,
      feeDescription,
      serviceDescription,
      refundDescription,
      environmentDescription,
    };
    const newBusinessProfile = new BusinessProfile(businessProfile);
    switch (prop) {
      case 'name':
        setName(value);
        newBusinessProfile.name = value;
        break;
      case 'owner':
        setOwner(value);
        newBusinessProfile.owner = value;
        break;
      case 'address':
        setAddress(value);
        newBusinessProfile.address = value;
        break;
      case 'tel':
        setTel(value);
        newBusinessProfile.tel = value;
        break;
      case 'priceDescription':
        setPriceDescription(value);
        newBusinessProfile.priceDescription = value;
        break;
      case 'paymentMethodDescription':
        setPaymentMethodDescription(value);
        newBusinessProfile.paymentMethodDescription = value;
        break;
      case 'paymentScheduleDescription':
        setPaymentScheduleDescription(value);
        newBusinessProfile.paymentScheduleDescription = value;
        break;
      case 'feeDescription':
        setFeeDescription(value);
        newBusinessProfile.feeDescription = value;
        break;
      case 'serviceDescription':
        setServiceDescription(value);
        newBusinessProfile.serviceDescription = value;
        break;
      case 'refundDescription':
        setRefundDescription(value);
        newBusinessProfile.refundDescription = value;
        break;
      case 'environmentDescription':
        setEnvironmentDescription(value);
        newBusinessProfile.environmentDescription = value;
        break;
      default:
        return;
    }
    const newErrors: ErrorList = {
      ...errors,
    };
    newBusinessProfile.isValid([prop]);
    const propErrors = newBusinessProfile.errors[prop];
    if (propErrors) {
      newErrors[prop] = propErrors;
    } else {
      delete newErrors[prop];
    }
    setErrors(newErrors);
    setIsDisabled(!newBusinessProfile.isValid());
  };

  const onCloseMessage = (e: SyntheticEvent) => {
    setMessage(null);
  };

  return (
    <div id={props.id}>
      <BusinessProfileForm
        errors={errors}
        onSubmit={onSubmit}
        onCancel={onCancel}
        onChange={onChange}
        onEdit={onEdit}
        isLoading={isLoading}
        viewer={props.viewer}
        isOpen={props.isOpen}
        isDisabled={isDisabled}
        name={name}
        owner={owner}
        address={address}
        tel={tel}
        priceDescription={priceDescription}
        paymentMethodDescription={paymentMethodDescription}
        paymentScheduleDescription={paymentScheduleDescription}
        feeDescription={feeDescription}
        serviceDescription={serviceDescription}
        refundDescription={refundDescription}
        environmentDescription={environmentDescription}
      />
      {!!message && (
        <Snackbar
          onClose={onCloseMessage}
          isOpen={true}
          severity={severity}
          message={message}
        />
      )}
    </div>
  );
});

export default Component;
